import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useBillQueue } from '@/hooks/useBillQueue';
import { useProcessingBridge } from '@/hooks/useProcessingBridge';
import { supabase } from '@/integrations/supabase/client';
import { EnhancedFileUpload } from '@/components/EnhancedFileUpload';
import { 
  Brain, 
  Upload, 
  Play, 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle, 
  Eye,
  Zap,
  Target,
  FileText
} from 'lucide-react';

export default function AITestCenter() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);
  const [selectedQueueItem, setSelectedQueueItem] = useState<string | null>(null);
  const { toast } = useToast();
  const { queueItems } = useBillQueue();
  const { processQueueItem } = useProcessingBridge();

  const handleTestProcessing = async (queueItemId: string) => {
    setIsProcessing(true);
    setSelectedQueueItem(queueItemId);
    
    try {
      console.log('Testing AI processing for queue item:', queueItemId);
      
      // Call the enhanced-process-bill function directly
      const { data, error } = await supabase.functions.invoke('enhanced-process-bill', {
        body: { queue_item_id: queueItemId }
      });

      if (error) {
        throw error;
      }

      console.log('Processing result:', data);
      setTestResults(data);
      
      toast({
        title: "Processing completed",
        description: "AI processing test completed successfully",
      });

      // Queue will update automatically via real-time subscriptions
      
    } catch (error) {
      console.error('Test processing failed:', error);
      toast({
        title: "Processing failed",
        description: error.message || 'Failed to process the bill',
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setSelectedQueueItem(null);
    }
  };

  const handleFileUploaded = async (filePath: string, fileName: string) => {
    toast({
      title: "File uploaded",
      description: `${fileName} uploaded successfully for testing`,
    });
    // Queue will update automatically
  };

  const pendingItems = queueItems?.filter(item => 
    item.processing_status === 'pending' && item.upload_status === 'uploaded'
  ) || [];

  const processedItems = queueItems?.filter(item => 
    item.processing_status !== 'pending'
  ) || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'default';
      case 'processing': return 'secondary';
      case 'pending': return 'outline';
      case 'error': return 'destructive';
      default: return 'outline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'processing': return <RefreshCw className="h-4 w-4 animate-spin" />;
      case 'pending': return <FileText className="h-4 w-4" />;
      case 'error': return <AlertTriangle className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">AI Test Center</h1>
          <p className="text-muted-foreground mt-2">
            Test and validate AI processing, matching, and auto-assignment features
          </p>
        </div>
        <Button onClick={() => window.location.reload()} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Test Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ready to Test</CardTitle>
            <Upload className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingItems.length}</div>
            <p className="text-xs text-muted-foreground">
              Uploaded files ready for processing
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">AI Processing</CardTitle>
            <Brain className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {processedItems.filter(item => item.processing_status === 'completed').length}
            </div>
            <p className="text-xs text-muted-foreground">
              Successfully processed by AI
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Auto-Assigned</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {processedItems.filter(item => item.auto_assigned).length}
            </div>
            <p className="text-xs text-muted-foreground">
              Automatically matched and assigned
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Need Review</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {processedItems.filter(item => item.requires_review).length}
            </div>
            <p className="text-xs text-muted-foreground">
              Require manual review
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="upload" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="upload">Upload Test Files</TabsTrigger>
          <TabsTrigger value="process">AI Processing Test</TabsTrigger>
          <TabsTrigger value="matching">Matching Demo</TabsTrigger>
          <TabsTrigger value="results">Test Results</TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Upload Test Files</CardTitle>
              <CardDescription>
                Upload utility bills to test the AI processing pipeline
              </CardDescription>
            </CardHeader>
            <CardContent>
              <EnhancedFileUpload onFileUploaded={handleFileUploaded} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="process" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>AI Processing Test</CardTitle>
              <CardDescription>
                Test AI extraction, matching, and auto-assignment on uploaded files
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File Name</TableHead>
                    <TableHead>Upload Status</TableHead>
                    <TableHead>Processing Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.file_name}</TableCell>
                      <TableCell>
                        <Badge variant="default">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {item.upload_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(item.processing_status)}>
                          {getStatusIcon(item.processing_status)}
                          <span className="ml-1">{item.processing_status}</span>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => handleTestProcessing(item.id)}
                          disabled={isProcessing && selectedQueueItem === item.id}
                          size="sm"
                        >
                          {isProcessing && selectedQueueItem === item.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <Play className="h-4 w-4 mr-2" />
                          )}
                          Test Process
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {pendingItems.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No files ready for testing. Upload a file first.
                </div>
              )}
            </CardContent>
          </Card>

          {testResults && (
            <Card>
              <CardHeader>
                <CardTitle>Latest Test Results</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-lg text-sm overflow-auto">
                  {JSON.stringify(testResults, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="matching" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Intelligent Matching Demo</CardTitle>
              <CardDescription>
                See how the AI matches extracted data to existing customers and accounts
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="text-center pb-2">
                    <Zap className="h-8 w-8 mx-auto text-green-500" />
                    <CardTitle className="text-lg">NEW-NEW</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-sm text-muted-foreground">
                      New customer, new account - requires full setup
                    </p>
                    <Badge variant="outline" className="mt-2">Manual Review</Badge>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="text-center pb-2">
                    <Target className="h-8 w-8 mx-auto text-blue-500" />
                    <CardTitle className="text-lg">OLD-NEW</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-sm text-muted-foreground">
                      Existing customer, new account - partial match
                    </p>
                    <Badge variant="secondary" className="mt-2">Auto-Assign</Badge>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="text-center pb-2">
                    <CheckCircle className="h-8 w-8 mx-auto text-primary" />
                    <CardTitle className="text-lg">OLD-OLD</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-sm text-muted-foreground">
                      Existing customer, existing account - full match
                    </p>
                    <Badge variant="default" className="mt-2">Auto-Assign</Badge>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="results" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Processing History</CardTitle>
              <CardDescription>
                View all processed files and their results
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Confidence</TableHead>
                    <TableHead>Matching Category</TableHead>
                    <TableHead>Auto-Assigned</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {processedItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.file_name}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(item.processing_status)}>
                          {getStatusIcon(item.processing_status)}
                          <span className="ml-1">{item.processing_status}</span>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {item.ai_confidence ? (
                          <div className="flex items-center space-x-2">
                            <Progress value={item.ai_confidence * 100} className="w-16" />
                            <span className="text-sm font-medium">
                              {Math.round(item.ai_confidence * 100)}%
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {item.matching_category || 'Unknown'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {item.auto_assigned ? (
                          <Badge variant="default">Yes</Badge>
                        ) : (
                          <Badge variant="outline">No</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {processedItems.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No processed files found. Test some files first.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}